export function formatDuration(
  ms: number,
  locale: string = "en-US",
  maxUnits: number = Infinity,
): string {
  const MS_PER_SECOND = 1000;
  const MS_PER_MINUTE = 60 * MS_PER_SECOND;
  const MS_PER_HOUR = 60 * MS_PER_MINUTE;
  const MS_PER_DAY = 24 * MS_PER_HOUR;
  const MS_PER_WEEK = 7 * MS_PER_DAY;
  const MS_PER_MONTH = 30 * MS_PER_DAY;
  const MS_PER_YEAR = 365 * MS_PER_DAY;

  let remainder = ms;

  const years = Math.floor(remainder / MS_PER_YEAR);
  remainder %= MS_PER_YEAR;

  const months = Math.floor(remainder / MS_PER_MONTH);
  remainder %= MS_PER_MONTH;

  const weeks = Math.floor(remainder / MS_PER_WEEK);
  remainder %= MS_PER_WEEK;

  const days = Math.floor(remainder / MS_PER_DAY);
  remainder %= MS_PER_DAY;

  const hours = Math.floor(remainder / MS_PER_HOUR);
  remainder %= MS_PER_HOUR;

  const minutes = Math.floor(remainder / MS_PER_MINUTE);
  remainder %= MS_PER_MINUTE;

  const seconds = Math.floor(remainder / MS_PER_SECOND);
  remainder %= MS_PER_SECOND;

  const millis = remainder;

  let allUnits = [
    { name: "years", value: years },
    { name: "months", value: months },
    { name: "weeks", value: weeks },
    { name: "days", value: days },
    { name: "hours", value: hours },
    { name: "minutes", value: minutes },
    { name: "seconds", value: seconds },
  ].filter((unit) => unit.value !== 0);

  if (allUnits.length > maxUnits) {
    allUnits = allUnits.slice(0, maxUnits);
  }

  if (typeof (Intl as any).DurationFormat === "function") {
    try {
      const formatter = new (Intl as any).DurationFormat(locale, { style: "short" });

      if (allUnits.length > 0) {
        const truncatedObject = allUnits.reduce(
          (acc, { name, value }) => {
            acc[name] = value;
            return acc;
          },
          {} as Record<string, number>,
        );

        const result = formatter.format(truncatedObject);
        if (result.trim().length > 0) {
          return result;
        }
      } else {
        if (millis > 0) {
          return `${millis}ms`;
        }
        return `0ms`;
      }
    } catch {
      // fallback if Intl.DurationFormat fails for any reason
    }
  }

  let yearLabel = "y";
  let monthLabel = "mo";
  let weekLabel = "w";
  let dayLabel = "d";
  let hourLabel = "h";
  let minuteLabel = "min";
  let secondLabel = "s";
  let millisecondLabel = "ms";

  const fetchShortUnit = (unit: Intl.RelativeTimeFormatUnit, fallback: string) => {
    try {
      const nf = new Intl.NumberFormat(locale, {
        style: "unit",
        unitDisplay: "short",
        unit: unit as unknown as string, // TS quirk
      });
      const parts = nf.formatToParts(1);
      const unitPart = parts.find((p) => p.type === "unit");
      return unitPart ? unitPart.value : fallback;
    } catch {
      return fallback;
    }
  };

  try {
    yearLabel = fetchShortUnit("year", yearLabel);
    monthLabel = fetchShortUnit("month", monthLabel);
    weekLabel = fetchShortUnit("week", weekLabel);
    dayLabel = fetchShortUnit("day", dayLabel);
    hourLabel = fetchShortUnit("hour", hourLabel);
    minuteLabel = fetchShortUnit("minute", minuteLabel);
    secondLabel = fetchShortUnit("second", secondLabel);
  } catch {
    // keep the fallback labels
  }

  if (allUnits.length === 0) {
    return millis > 0 ? `${millis}${millisecondLabel}` : `0${millisecondLabel}`;
  }

  const outputParts: string[] = [];
  for (const { name, value } of allUnits) {
    switch (name) {
      case "years":
        outputParts.push(`${value}${yearLabel}`);
        break;
      case "months":
        outputParts.push(`${value}${monthLabel}`);
        break;
      case "weeks":
        outputParts.push(`${value}${weekLabel}`);
        break;
      case "days":
        outputParts.push(`${value}${dayLabel}`);
        break;
      case "hours":
        outputParts.push(`${value}${hourLabel}`);
        break;
      case "minutes":
        outputParts.push(`${value}${minuteLabel}`);
        break;
      case "seconds":
        outputParts.push(`${value}${secondLabel}`);
        break;
      default:
        // should never happen
        break;
    }
  }

  return outputParts.join(" ");
}
